/**
 * Formatting shared by every usage surface.
 *
 * Two number forms on purpose. `fmt` is the exact figure, and it is what a table
 * cell and a tooltip carry — someone reconciling a bill needs the digits.
 * `fmtCompact` is for the headline tiles and the chart axes, where "1.55M" reads
 * in one glance and "1,550,000" does not. Every compact number is paired with
 * the exact one in a `title` attribute, so the detail is never lost, only
 * folded.
 */

export const fmt = (n) => Number(n ?? 0).toLocaleString();

export const fmtCompact = (n) => {
  const value = Number(n ?? 0);
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(abs >= 10_000_000_000 ? 0 : 1)}B`;
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${Math.round(value / 1000)}K`;
  if (abs >= 1_000) return `${(value / 1000).toFixed(1)}K`;
  return fmt(value);
};

/** Share of a total, as a rounded percent. Guards the zero-total case. */
export const pctOf = (part, total) => {
  const t = Number(total ?? 0);
  if (!t) return 0;
  return (Number(part ?? 0) / t) * 100;
};

export const fmtPct = (p) => {
  const value = Number(p ?? 0);
  if (value > 0 && value < 0.1) return "<0.1%";
  return `${value.toFixed(value >= 10 ? 0 : 1)}%`;
};

/**
 * What to call a tenant in a usage report.
 *
 * Usage rows outlive the tenant, so a row can name a tenant that has no row in
 * `tenants` left to read a name or a slug from. The server sends null for both
 * rather than inventing a stand-in, which leaves the last word to the surface —
 * and a blank cell is not it. The id prefix is enough to tell two such rows
 * apart and to look one up.
 *
 * Every usage surface calls this, so a nameless tenant reads the same on the
 * fleet table, in the picker and on the chart.
 */
export const tenantName = (name, slug, id) => name || slug || (id ? `${String(id).slice(0, 8)}…` : "Unknown tenant");
